import { useMemo, useState, type ReactNode } from 'react';
import { FieldEditor } from './FieldEditor';
import { useT, type Locale, type MessageKey } from '../lib/i18n';
import { labelOf, type FieldDefinition, type FieldType, type RegistryDefinition } from '../lib/registry';
import {
  EFFECT_KINDS, OPERATORS, STATE_ROLES, actionsOf, flowOf, lineage, readCondition, writeCondition,
  type ActionDefinition, type Condition, type Effect, type EffectKind, type Operator, type Precondition
} from '../lib/flowDesign';
import { KindIcon } from './icons';

const PARAMETER_TYPES: FieldType[] = ['text', 'integer', 'decimal', 'money', 'boolean', 'date', 'choice', 'reference'];
const PERMISSIONS = ['move', 'update', 'decide', 'issue', 'create', 'delete'];
const LANGUAGES: Locale[] = ['en', 'tr', 'nl', 'de'];

type Keyed = { key: string; labels?: Record<string, string>; [other: string]: unknown };
const list = (definition: RegistryDefinition, name: string): Keyed[] => {
  const v = (definition as Record<string, unknown>)[name];
  return Array.isArray(v) ? v as Keyed[] : [];
};

/** The fields an expression about a template may read: its own, those above it, and what every instance has. */
export function fieldsFor(definition: RegistryDefinition, typeKey: string | undefined): { key: string; label: string; type: string }[] {
  if (!typeKey) return [];
  const types = new Map(definition.types.map((t) => [t.key, t]));
  const out: { key: string; label: string; type: string }[] = [];
  for (const k of lineage(definition, typeKey)) {
    for (const f of types.get(k)?.fields ?? []) {
      if (!out.some((o) => o.key === f.key)) out.push({ key: f.key, label: labelOf(f.labels, 'en', f.key), type: f.type });
    }
  }
  return out;
}

/**
 * Words in the languages of the platform: the one the person reads in, the rest a click away.
 * What is not written in one is shown in another, so nothing is said empty.
 */
export function Words({ value, onChange, label, testid, multiline }: {
  value?: Record<string, string>; onChange: (next: Record<string, string>) => void; label: string; testid?: string; multiline?: boolean;
}) {
  const { t, locale } = useT();
  const [all, setAll] = useState(false);
  const set = (language: string, text: string) => {
    const next = { ...(value ?? {}), [language]: text };
    if (!text) delete next[language];
    onChange(next);
  };
  const shown = all ? LANGUAGES : [locale];
  return (
    <div className="tx-words">
      <div className="tx-words-head"><span>{label}</span>
        <button className="link" type="button" onClick={() => setAll(!all)}>
          {all ? t('tx.words.fewer') : t('tx.words.all', { count: LANGUAGES.filter((l) => value?.[l]).length })}
        </button>
      </div>
      {shown.map((language) => (
        <label className="tx-words-row" key={language}>
          {all && <span className="tx-lang">{language.toUpperCase()}</span>}
          {multiline
            ? <textarea rows={2} value={value?.[language] ?? ''} data-testid={testid ? `${testid}-${language}` : undefined}
                        placeholder={labelOf(value, language, '')} onChange={(e) => set(language, e.target.value)} />
            : <input value={value?.[language] ?? ''} data-testid={testid ? `${testid}-${language}` : undefined}
                     placeholder={labelOf(value, language, '')} onChange={(e) => set(language, e.target.value)} />}
        </label>
      ))}
    </div>
  );
}

/**
 * A condition, built by picking - a field, how it compares, with what - or written in the
 * taxonomy's expression language when it is more than that. What the builder cannot say is kept
 * as written and shown as written.
 */
export function ConditionField({ value, onChange, fields, parameters, testid }: {
  value: string; onChange: (next: string) => void;
  fields: { key: string; label: string }[]; parameters: string[]; testid?: string;
}) {
  const { t } = useT();
  const parsed = useMemo(() => (value.trim() === '' ? { field: fields[0]?.key ?? '', op: 'filled' as Operator, value: '' } : readCondition(value)), [value, fields]);
  const [writing, setWriting] = useState(parsed == null);
  const names = [...fields.map((f) => ({ key: f.key, label: `${f.label} (${f.key})` })), { key: 'id', label: 'id' },
    ...parameters.map((p) => ({ key: `$${p}`, label: `$${p}` }))];
  if (writing || !parsed) {
    return (
      <div className="tx-cond">
        <input className="mono" value={value} spellCheck={false} data-testid={testid} list="tx-cond-names"
               placeholder={t('tx.cond.placeholder')} onChange={(e) => onChange(e.target.value)} />
        <datalist id="tx-cond-names">{names.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}</datalist>
        {readCondition(value) && <button type="button" className="link" onClick={() => setWriting(false)}>{t('tx.cond.simple')}</button>}
      </div>
    );
  }
  const set = (patch: Partial<Condition>) => onChange(writeCondition({ ...parsed, ...patch }));
  const roleOp = parsed.op === 'role' || parsed.op === 'not-role';
  return (
    <div className="tx-cond">
      {!roleOp && (
        <select value={parsed.field} data-testid={testid ? `${testid}-field` : undefined} onChange={(e) => set({ field: e.target.value })}>
          {!names.some((n) => n.key === parsed.field) && <option value={parsed.field}>{parsed.field}</option>}
          {names.map((n) => <option key={n.key} value={n.key}>{n.label}</option>)}
        </select>
      )}
      <select value={parsed.op} data-testid={testid ? `${testid}-op` : undefined}
              onChange={(e) => set({ op: e.target.value as Operator, value: ['role', 'not-role'].includes(e.target.value) ? (roleOp ? parsed.value : 'lost') : parsed.value })}>
        {OPERATORS.map((op) => <option key={op} value={op}>{t(`tx.op.${op}` as MessageKey)}</option>)}
      </select>
      {roleOp
        ? <select value={parsed.value} onChange={(e) => set({ value: e.target.value })}>
            {STATE_ROLES.map((r) => <option key={r} value={r}>{t(`types.stateRole.${r}` as MessageKey)}</option>)}
          </select>
        : parsed.op !== 'empty' && parsed.op !== 'filled'
          && <input value={parsed.value} data-testid={testid ? `${testid}-value` : undefined} onChange={(e) => set({ value: e.target.value })} />}
      <button type="button" className="link" onClick={() => setWriting(true)}>{t('tx.cond.write')}</button>
    </div>
  );
}

/** A list of conditions, each with what is said when it does not hold. */
function Conditions({ items, onChange, fields, parameters, saying, addLabel, testid }: {
  items: Precondition[]; onChange: (next: Precondition[]) => void;
  fields: { key: string; label: string }[]; parameters: string[]; saying: string; addLabel: string; testid: string;
}) {
  const { t, locale } = useT();
  return (
    <div className="tx-conds">
      {items.map((p, i) => (
        <div className="tx-cond-card" key={i} data-testid={`${testid}-${i}`}>
          <ConditionField value={p.when} fields={fields} parameters={parameters} testid={`${testid}-${i}-when`}
                          onChange={(when) => onChange(items.map((x, at) => (at === i ? { ...x, when } : x)))} />
          <label className="tx-inline">
            <span>{saying}</span>
            <input value={p.refusal?.[locale] ?? ''} placeholder={labelOf(p.refusal, locale, '')}
                   onChange={(e) => onChange(items.map((x, at) => (at === i ? { ...x, refusal: { ...(x.refusal ?? {}), [locale]: e.target.value } } : x)))} />
          </label>
          <button type="button" className="ghost tx-x" aria-label={t('tx.remove')} onClick={() => onChange(items.filter((_, at) => at !== i))}>✕</button>
        </div>
      ))}
      <button type="button" className="tx-add" data-testid={`${testid}-add`}
              onClick={() => onChange([...items, { when: '' }])}>+ {addLabel}</button>
    </div>
  );
}

/** One effect in a few words, for its folded card and the action's sentence. */
export function effectSentence(effect: Effect, definition: RegistryDefinition, target: string | undefined, locale: Locale,
                               t: (key: MessageKey, vars?: Record<string, string | number>) => string): string {
  const typeLabel = (k?: string) => labelOf(definition.types.find((x) => x.key === k)?.labels, locale, k ?? '?');
  const flow = flowOf(definition, target);
  const stateLabel = (name?: string) => {
    if (!name) return '?';
    if (name.startsWith('@')) return t(`types.stateRole.${name.slice(1)}` as MessageKey);
    return labelOf(flow?.states.find((s) => s.key === name)?.labels, locale, name);
  };
  const on = effect.on ? ` (${effect.on})` : '';
  switch (effect.kind) {
    case 'move': return t('tx.effect.say.move', { state: stateLabel(effect.state) }) + on;
    case 'set': return t('tx.effect.say.set', { field: effect.field ?? '?', value: effect.value ?? '' }) + on;
    case 'create': return t('tx.effect.say.create', { template: typeLabel(effect.template) }) + (effect.as ? ` → ${effect.as}` : '') + on;
    case 'note': return t('tx.effect.say.note', { text: labelOf(effect.labels, locale, effect.value ?? '') });
    case 'message': return t('tx.effect.say.message', { message: effect.message ?? '?' });
    case 'link': return t('tx.effect.say.link', { link: effect.link ?? '?', to: effect.to ?? '?' }) + on;
    case 'unlink': return t('tx.effect.say.unlink', { link: effect.link ?? '?', to: effect.to ?? '?' }) + on;
    case 'compute': return t('tx.effect.say.compute', { binding: effect.binding ?? '?' });
    case 'emit': return t('tx.effect.say.emit', { event: effect.event ?? '?' });
    case 'issue-document': return t('tx.effect.say.issueDocument', { template: effect.template ?? '?' });
    default: return t(`tx.effect.${effect.kind}` as MessageKey);
  }
}

/** One step of what an action does: its kind, and what that kind asks. */
function EffectCard({ effect, index, count, definition, target, parameters, made, onChange, onMove, onRemove }: {
  effect: Effect; index: number; count: number; definition: RegistryDefinition; target?: string; parameters: string[];
  /** What earlier steps made, by the name they gave it. */
  made: string[];
  onChange: (next: Effect) => void; onMove: (by: number) => void; onRemove: () => void;
}) {
  const { t, locale } = useT();
  const [open, setOpen] = useState(false);
  const set = (patch: Partial<Effect>) => onChange(tidyEffect({ ...effect, ...patch }));
  const fields = fieldsFor(definition, target);
  const flow = flowOf(definition, target);
  const lineageKeys = target ? lineage(definition, target) : [];
  const links = (definition.links ?? []).filter((l) => lineageKeys.includes(l.from) || lineageKeys.includes(l.to));
  const refs = [...parameters.map((p) => `$${p}`), ...made.map((m) => `$${m}`)];
  const field = (label: string, control: ReactNode) => <label className="field"><span>{label}</span>{control}</label>;
  const expression = (value: string | undefined, onSet: (v: string) => void, testid?: string) => (
    <input className="mono" value={value ?? ''} spellCheck={false} list="tx-effect-names" data-testid={testid}
           onChange={(e) => onSet(e.target.value)} />
  );

  return (
    <li className={`tx-step${open ? ' open' : ''}`} data-testid={`tx-effect-${index}`}>
      <div className="tx-step-head">
        <span className="tx-step-no">{index + 1}</span>
        <button type="button" className="tx-step-say" onClick={() => setOpen(!open)} aria-expanded={open}>
          <span className={`tx-effect-chip tx-effect-${effect.kind}`}>{t(`tx.effect.${effect.kind}` as MessageKey)}</span>
          <span>{effectSentence(effect, definition, target, locale, t)}</span>
        </button>
        <button type="button" className="ghost icon" disabled={index === 0} aria-label={t('tx.up')} onClick={() => onMove(-1)}>↑</button>
        <button type="button" className="ghost icon" disabled={index === count - 1} aria-label={t('tx.down')} onClick={() => onMove(1)}>↓</button>
        <button type="button" className="ghost icon" aria-label={t('tx.remove')} onClick={onRemove}>✕</button>
      </div>
      {open && (
        <div className="tx-step-body">
          <datalist id="tx-effect-names">
            {[...fields.map((f) => f.key), 'id', ...refs].map((n) => <option key={n} value={n} />)}
          </datalist>
          {field(t('tx.effect.kind'), (
            <select value={effect.kind} data-testid={`tx-effect-${index}-kind`} onChange={(e) => onChange({ kind: e.target.value as EffectKind })}>
              {EFFECT_KINDS.map((k) => <option key={k} value={k}>{t(`tx.effect.${k}` as MessageKey)}</option>)}
            </select>
          ))}
          <p className="hint">{t(`tx.effect.hint.${effect.kind}` as MessageKey)}</p>
          {effect.kind === 'move' && field(t('tx.effect.state'), (
            <select value={effect.state ?? ''} data-testid={`tx-effect-${index}-state`} onChange={(e) => set({ state: e.target.value })}>
              <option value="">{t('taxonomy.choose')}</option>
              {(flow?.states ?? []).map((s) => <option key={s.key} value={s.key}>{labelOf(s.labels, locale, s.key)}</option>)}
              {STATE_ROLES.map((r) => <option key={r} value={`@${r}`}>{t('tx.anyRole', { role: t(`types.stateRole.${r}` as MessageKey) })}</option>)}
            </select>
          ))}
          {effect.kind === 'set' && (
            <div className="field-row">
              {field(t('tx.effect.field'), (
                <select value={effect.field ?? ''} onChange={(e) => set({ field: e.target.value })}>
                  <option value="">{t('taxonomy.choose')}</option>
                  {fields.map((f) => <option key={f.key} value={f.key}>{f.label} ({f.key})</option>)}
                </select>
              ))}
              {field(t('tx.effect.value'), expression(effect.value, (value) => set({ value })))}
            </div>
          )}
          {effect.kind === 'create' && (
            <>
              <div className="field-row">
                {field(t('tx.effect.template'), (
                  <select value={effect.template ?? ''} onChange={(e) => set({ template: e.target.value })}>
                    <option value="">{t('taxonomy.choose')}</option>
                    {definition.types.filter((x) => !x.abstract).map((x) => <option key={x.key} value={x.key}>{labelOf(x.labels, locale, x.key)} ({x.key})</option>)}
                  </select>
                ))}
                {field(t('tx.effect.as'), <input value={effect.as ?? ''} spellCheck={false} onChange={(e) => set({ as: e.target.value.trim() })} />)}
              </div>
              {field(t('tx.effect.link'), (
                <select value={effect.link ?? ''} onChange={(e) => set({ link: e.target.value })}>
                  <option value="">{t('tx.effect.noLink')}</option>
                  {(definition.links ?? []).map((l) => <option key={l.key} value={l.key}>{l.key}</option>)}
                </select>
              ))}
              <div className="tx-values">
                <span className="hint">{t('tx.effect.values')}</span>
                {Object.entries(effect.values ?? {}).map(([k, v]) => (
                  <div className="tx-value-row" key={k}>
                    <select value={k} onChange={(e) => {
                      const next = { ...(effect.values ?? {}) }; delete next[k]; next[e.target.value] = v; set({ values: next });
                    }}>
                      <option value={k}>{k}</option>
                      {fieldsFor(definition, effect.template).filter((f) => !(k === f.key) && !(f.key in (effect.values ?? {})))
                        .map((f) => <option key={f.key} value={f.key}>{f.label} ({f.key})</option>)}
                    </select>
                    <span>=</span>
                    {expression(v, (value) => set({ values: { ...(effect.values ?? {}), [k]: value } }))}
                    <button type="button" className="ghost icon" aria-label={t('tx.remove')} onClick={() => {
                      const next = { ...(effect.values ?? {}) }; delete next[k]; set({ values: next });
                    }}>✕</button>
                  </div>
                ))}
                {effect.template && fieldsFor(definition, effect.template).some((f) => !(f.key in (effect.values ?? {}))) && (
                  <button type="button" className="tx-add" onClick={() => {
                    const free = fieldsFor(definition, effect.template).find((f) => !(f.key in (effect.values ?? {})));
                    if (free) set({ values: { ...(effect.values ?? {}), [free.key]: '' } });
                  }}>+ {t('tx.effect.addValue')}</button>
                )}
              </div>
              <label className="field-check"><input type="checkbox" checked={effect.ifInstalled === true}
                     onChange={(e) => set({ ifInstalled: e.target.checked || undefined })} /> {t('tx.effect.ifInstalled')}</label>
            </>
          )}
          {(effect.kind === 'link' || effect.kind === 'unlink') && (
            <div className="field-row">
              {field(t('tx.effect.link'), (
                <select value={effect.link ?? ''} onChange={(e) => set({ link: e.target.value })}>
                  <option value="">{t('taxonomy.choose')}</option>
                  {links.map((l) => <option key={l.key} value={l.key}>{labelOf(l.labels, locale, l.key)} ({l.from} → {l.to})</option>)}
                  {parameters.map((p) => <option key={p} value={`$${p}`}>${p}</option>)}
                </select>
              ))}
              {field(t('tx.effect.to'), expression(effect.to, (to) => set({ to })))}
            </div>
          )}
          {effect.kind === 'repoint' && field(t('tx.effect.to'), expression(effect.to, (to) => set({ to })))}
          {effect.kind === 'note' && (
            <Words label={t('tx.effect.noteText')} value={effect.labels} multiline onChange={(labels) => set({ labels })} />
          )}
          {effect.kind === 'message' && (
            <div className="field-row">
              {field(t('tx.effect.message'), (
                <select value={effect.message ?? ''} onChange={(e) => set({ message: e.target.value })}>
                  <option value="">{t('taxonomy.choose')}</option>
                  {list(definition, 'messages').map((m) => {
                    const k = m.key.replace(/^message\./, '');
                    return <option key={m.key} value={k}>{labelOf(m.labels, locale, k)} ({k})</option>;
                  })}
                </select>
              ))}
              {field(t('tx.effect.address'), expression(effect.to, (to) => set({ to })))}
            </div>
          )}
          {effect.kind === 'compute' && field(t('tx.effect.binding'), (
            <select value={effect.binding ?? ''} onChange={(e) => set({ binding: e.target.value })}>
              <option value="">{t('taxonomy.choose')}</option>
              {list(definition, 'bindings').map((b) => <option key={b.key} value={b.key}>{b.key}</option>)}
            </select>
          ))}
          {effect.kind === 'emit' && field(t('tx.effect.event'), <input value={effect.event ?? ''} spellCheck={false} onChange={(e) => set({ event: e.target.value.trim() })} />)}
          {effect.kind === 'issue-document' && (
            <div className="field-row">
              {field(t('tx.effect.documentTemplate'), (
                <select value={effect.template ?? ''} onChange={(e) => set({ template: e.target.value })}>
                  <option value="">{t('taxonomy.choose')}</option>
                  {list(definition, 'documents').map((d) => {
                    const k = d.key.replace(/^document\./, '');
                    return <option key={d.key} value={k}>{labelOf(d.labels, locale, k)}</option>;
                  })}
                </select>
              ))}
              {field(t('tx.effect.as'), <input value={effect.as ?? ''} spellCheck={false} onChange={(e) => set({ as: e.target.value.trim() })} />)}
            </div>
          )}
          {effect.kind !== 'compute' && effect.kind !== 'emit' && field(t('tx.effect.on'), (
            <select value={effect.on ?? ''} onChange={(e) => set({ on: e.target.value || undefined })}>
              <option value="">{t('tx.effect.onTarget')}</option>
              {made.map((m) => <option key={m} value={m}>{t('tx.effect.onMade', { name: m })}</option>)}
              {parameters.map((p) => <option key={p} value={`$${p}`}>{t('tx.effect.onParameter', { name: p })}</option>)}
              {effect.on && !made.includes(effect.on) && !parameters.some((p) => `$${p}` === effect.on) && <option value={effect.on}>{effect.on}</option>}
            </select>
          ))}
        </div>
      )}
    </li>
  );
}

/** An effect without the parts its kind does not read, so that what is saved is what is shown. */
function tidyEffect(effect: Effect): Effect {
  const out: Effect = { ...effect };
  for (const k of Object.keys(out) as (keyof Effect)[]) {
    const v = out[k];
    if (v === '' || v === undefined || (typeof v === 'object' && v !== null && Object.keys(v).length === 0)) delete out[k];
  }
  return out;
}

/**
 * An action as a person writes it (docs/en/47 §4): what its button says and what it runs on,
 * whether a person presses it or it runs by itself when a record enters a state, what it asks,
 * when it may run and what it warns about, and what it does - a list of steps from the closed
 * vocabulary, each said in words. Never a script, never JSON: the same envelope the server runs.
 */
export function ActionEditor({ definition, actionKey, onChange, onRemove, published }: {
  definition: RegistryDefinition;
  actionKey: string;
  onChange: (next: RegistryDefinition) => void;
  onRemove?: () => void;
  /** Whether the published release has it already: its key is then fixed. */
  published: boolean;
}) {
  const { t, locale } = useT();
  const actions = actionsOf(definition);
  const action = actions.find((a) => a.key === actionKey);
  const [tab, setTab] = useState<'does' | 'asks' | 'when'>('does');
  if (!action) return <div className="empty">{t('taxonomy.gone')}</div>;
  const set = (patch: Partial<ActionDefinition>) => {
    const next = { ...action, ...patch } as ActionDefinition;
    for (const k of Object.keys(next)) {
      const v = next[k];
      if (v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) delete next[k];
    }
    onChange({ ...definition, actions: actions.map((a) => (a.key === actionKey ? next : a)) } as RegistryDefinition);
  };
  const target = action.target;
  const flow = flowOf(definition, target);
  const fields = fieldsFor(definition, target);
  const parameters = action.parameters ?? [];
  const paramKeys = parameters.map((p) => p.key).filter(Boolean);
  const effects = action.effects ?? [];
  const automatic = action.when?.on === 'entered';
  const made = (upTo: number) => effects.slice(0, upTo).map((e) => e.as).filter((x): x is string => !!x);
  const templates = definition.types.filter((x) => !x.abstract || x.key === target);

  return (
    <div className="tx-action" data-testid="tx-action">
      <header className="tx-action-head">
        <div className={`tx-button-preview ${action.style ?? ''}`}>{labelOf(action.labels, locale, action.key)}</div>
        <code>{action.key}</code>
        {!published && <span className="badge warn">{t('types.notPublished')}</span>}
      </header>
      <p className="tx-sentence" data-testid="tx-action-sentence">
        {automatic
          ? t('tx.action.sayAuto', { state: action.when?.state ?? '?', template: labelOf(definition.types.find((x) => x.key === target)?.labels, locale, target ?? '?') })
          : t('tx.action.sayButton', { template: labelOf(definition.types.find((x) => x.key === target)?.labels, locale, target ?? '?') })}
        {parameters.length > 0 && <> {t('tx.action.sayAsks', { what: parameters.map((p) => labelOf(p.labels, locale, p.key)).join(', ') })}</>}
        {(action.preconditions ?? []).length > 0 && <> {t('tx.action.sayOnly', { count: (action.preconditions ?? []).length })}</>}
        {' '}{effects.length > 0 ? t('tx.action.sayDoes', { what: effects.map((e) => effectSentence(e, definition, target, locale, t)).join('; ') }) : t('tx.action.sayNothing')}
      </p>

      <Words label={t('tx.action.buttonText')} value={action.labels} testid="tx-action-label" onChange={(labels) => set({ labels })} />
      <div className="field-row">
        <label className="field"><span>{t('tx.action.runsOn')}</span>
          <select value={target ?? ''} data-testid="tx-action-target" onChange={(e) => set({ target: e.target.value })}>
            <option value="">{t('taxonomy.choose')}</option>
            {templates.map((x) => <option key={x.key} value={x.key}>{labelOf(x.labels, locale, x.key)} ({x.key})</option>)}
          </select></label>
        <label className="field"><span>{t('tx.action.permission')}</span>
          <input value={action.permission ?? ''} list="tx-permissions" spellCheck={false} data-testid="tx-action-permission"
                 onChange={(e) => set({ permission: e.target.value.trim() || undefined })} />
          <datalist id="tx-permissions">{PERMISSIONS.map((p) => <option key={p} value={p}>{t(`tx.permission.${p}` as MessageKey)}</option>)}</datalist>
        </label>
      </div>

      <div className="tx-segment wide" role="radiogroup" aria-label={t('tx.action.trigger')}>
        <button type="button" role="radio" aria-checked={!automatic} className={!automatic ? 'on' : ''} data-testid="tx-action-manual"
                onClick={() => set({ when: undefined })}>
          <KindIcon kind="actions" /> {t('tx.action.manual')}</button>
        <button type="button" role="radio" aria-checked={automatic} className={automatic ? 'on' : ''} data-testid="tx-action-auto"
                onClick={() => set({ when: { on: 'entered', state: action.when?.state ?? flow?.states[0]?.key ?? '' } })}>
          <KindIcon kind="automation" /> {t('tx.action.automatic')}</button>
      </div>
      {automatic && (
        <label className="field"><span>{t('tx.action.whenEnters')}</span>
          <select value={action.when?.state ?? ''} data-testid="tx-action-when-state" onChange={(e) => set({ when: { on: 'entered', state: e.target.value } })}>
            {(flow?.states ?? []).map((s) => <option key={s.key} value={s.key}>{labelOf(s.labels, locale, s.key)}</option>)}
            {STATE_ROLES.map((r) => <option key={r} value={`@${r}`}>{t('tx.anyRole', { role: t(`types.stateRole.${r}` as MessageKey) })}</option>)}
          </select>
          {!flow && <span className="hint problem">{t('tx.action.noFlow')}</span>}
        </label>
      )}
      {!automatic && (
        <div className="tx-segment small" role="radiogroup" aria-label={t('tx.action.style')}>
          {(['', 'primary', 'danger'] as const).map((s) => (
            <button type="button" key={s || 'plain'} role="radio" aria-checked={(action.style ?? '') === s} className={(action.style ?? '') === s ? 'on' : ''}
                    onClick={() => set({ style: s || undefined })}>{t(`tx.style.${s || 'plain'}` as MessageKey)}</button>
          ))}
        </div>
      )}

      <nav className="tx-tabs" role="tablist">
        {(['does', 'asks', 'when'] as const).map((k) => (
          <button key={k} type="button" role="tab" aria-selected={tab === k} className={tab === k ? 'on' : ''} data-testid={`tx-action-tab-${k}`}
                  onClick={() => setTab(k)}>
            {t(`tx.action.tab.${k}` as MessageKey)}
            <span className="tx-count">{k === 'does' ? effects.length : k === 'asks' ? parameters.length : (action.preconditions ?? []).length + (action.warnings ?? []).length}</span>
          </button>
        ))}
      </nav>

      {tab === 'does' && (
        <section>
          <p className="hint">{t('tx.action.doesHint')}</p>
          <ol className="tx-steps">
            {effects.map((effect, i) => (
              <EffectCard key={i} effect={effect} index={i} count={effects.length} definition={definition} target={target}
                          parameters={paramKeys} made={made(i)}
                          onChange={(next) => set({ effects: effects.map((e, at) => (at === i ? next : e)) })}
                          onMove={(by) => {
                            const next = [...effects];
                            const [moved] = next.splice(i, 1);
                            next.splice(i + by, 0, moved);
                            set({ effects: next });
                          }}
                          onRemove={() => set({ effects: effects.filter((_, at) => at !== i) })} />
            ))}
          </ol>
          <div className="tx-palette">
            {(['move', 'set', 'create', 'note', 'message', 'link', 'issue-document', 'compute', 'emit'] as EffectKind[]).map((k) => (
              <button type="button" key={k} className={`tx-effect-chip tx-effect-${k}`} data-testid={`tx-add-effect-${k}`}
                      onClick={() => set({ effects: [...effects, { kind: k }] })}>+ {t(`tx.effect.${k}` as MessageKey)}</button>
            ))}
          </div>
        </section>
      )}

      {tab === 'asks' && (
        <section>
          <p className="hint">{t('tx.action.asksHint')}</p>
          {parameters.map((p, i) => (
            <FieldEditor key={i} field={p} kinds={PARAMETER_TYPES} wording="record" published={false}
                         targets={definition.types.map((x) => ({ key: x.key, label: `${labelOf(x.labels, locale, x.key)} (${x.key})` }))}
                         onChange={(next: FieldDefinition) => set({ parameters: parameters.map((x, at) => (at === i ? next : x)) })}
                         onRemove={() => set({ parameters: parameters.filter((_, at) => at !== i) })} />
          ))}
          <button type="button" className="tx-add" data-testid="tx-add-parameter"
                  onClick={() => set({ parameters: [...parameters, { key: '', type: 'text' }] })}>+ {t('tx.action.addQuestion')}</button>
          {parameters.length > 0 && (
            <div className="tx-dialog-preview" aria-label={t('tx.action.dialogPreview')}>
              <div className="tx-dialog-title">{labelOf(action.labels, locale, action.key)}</div>
              {parameters.map((p, i) => (
                <label className="field" key={i}><span>{labelOf(p.labels, locale, p.key)}{p.required ? ' *' : ''}</span><input disabled /></label>
              ))}
              <div className="tx-dialog-buttons"><span className={`tx-button-preview ${action.style ?? 'primary'}`}>{labelOf(action.labels, locale, action.key)}</span></div>
            </div>
          )}
        </section>
      )}

      {tab === 'when' && (
        <section>
          <h4>{t('tx.action.onlyWhen')}</h4>
          <p className="hint">{t('tx.action.onlyWhenHint')}</p>
          <Conditions items={action.preconditions ?? []} fields={fields} parameters={paramKeys} testid="tx-pre"
                      saying={t('tx.action.otherwiseSay')} addLabel={t('tx.action.addCondition')}
                      onChange={(preconditions) => set({ preconditions })} />
          <h4>{t('tx.action.warn')}</h4>
          <p className="hint">{t('tx.action.warnHint')}</p>
          <Conditions items={action.warnings ?? []} fields={fields} parameters={paramKeys} testid="tx-warn"
                      saying={t('tx.action.warnSay')} addLabel={t('tx.action.addWarning')}
                      onChange={(warnings) => set({ warnings })} />
        </section>
      )}

      {onRemove && (
        <div className="tx-danger-zone">
          <button type="button" className="ghost danger-text" data-testid="tx-action-remove" onClick={onRemove}>{t('tx.action.remove')}</button>
        </div>
      )}
    </div>
  );
}
